import { makeAutoObservable, runInAction } from "mobx";
import apiHandler from "../handlers/ApiHandler";
import deviceTypeStore from "./DeviceTypeStore";
import locationStore from "./LocationStore";
import regionStore from "./RegionStore";
import authStore from "./AuthStore";

class DeviceStore {
  devices = [];
  isLoading = false;
  error = null;
  errorMessage = null;
  selectedDevice = null;
  pagination = {};
  deviceNameMap = {};
  deviceNameRefreshInterval = null;
  lastFetchedDeviceId = 0;
  refreshInterval = null;
  refreshTimer = null;

  constructor() {
    makeAutoObservable(this);
    this.startDeviceNameAutoRefresh();
  }

  startAutoRefresh() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);

    this.refreshTimer = setInterval(
      () => {
        this.loadDevices();
      },
      this.refreshInterval * 60 * 1000
    );
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  setRefreshInterval(minutes) {
    this.refreshInterval = minutes;
    if (minutes === null) {
      this.stopAutoRefresh();
    } else {
      this.startAutoRefresh();
    }
  }

  async loadDevices() {
    this.setLoading(true);
    await Promise.all([
      deviceTypeStore.fetchDeviceTypes(),
      locationStore.fetchLocations(),
      regionStore.fetchRegions(),
    ]);
    try {
      const data = await apiHandler.get("/api/devices");
      console.log("Here is all device data in device store: ", data);
      runInAction(() => {
        const defaultTypeId = deviceTypeStore.defaultTypeId;
        const defaultLocationId = locationStore.defaultLocationId;
        const defaultRegionId = regionStore.defaultRegionId;

        this.devices = data.map((device) => {
          const isDefaultType = device.type_id === defaultTypeId;
          const isDefaultLocation = device.location_id === defaultLocationId;
          const isDefaultRegion = device.region_id === defaultRegionId;
          const isPendingName =
            typeof device.name === "string" &&
            device.name.trim().toLowerCase().startsWith("pending device");

          // console.log("Default Type ID:", deviceTypeStore.defaultTypeId);
          // console.log("Default Location ID:", locationStore.defaultLocationId);

          return {
            ...device,
            missingFields: [
              isDefaultType ? "Device Type" : null,
              isDefaultLocation ? "Location" : null,
              isPendingName ? "Name" : null,
              isDefaultRegion ? "Region" : null,
            ].filter(Boolean),
          };
        });
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  get pendingDevices() {
    const defaultTypeId = deviceTypeStore.defaultTypeId;
    const defaultLocationId = locationStore.defaultLocationId;
    const defaultRegionId = regionStore.defaultRegionId;
    return this.devices.filter(
      (device) =>
        (device.name && device.name.toLowerCase().includes("pending device")) ||
        device.type_id === defaultTypeId ||
        device.location_id === defaultLocationId ||
        device.region_id === defaultRegionId
    );
  }

  get registeredDevices() {
    const defaultTypeId = deviceTypeStore.defaultTypeId;
    const defaultLocationId = locationStore.defaultLocationId;
    return this.devices.filter(
      (device) =>
        device.type_id !== defaultTypeId &&
        device.location_id !== defaultLocationId
    );
  }

  async loadDeviceById(deviceId) {
    const existingDevice = this.devices.find((d) => d.id === Number(deviceId));

    if (existingDevice) {
      runInAction(() => {
        this.selectedDevice = existingDevice;
      });
      return existingDevice;
    }

    this.setLoading(true);
    try {
      const device = await apiHandler.get(`/api/devices/${deviceId}`);
      runInAction(() => {
        this.selectedDevice = device;

        // Ensure that the devices list contains this device
        if (!this.devices.some((d) => d.id === device.id)) {
          this.devices.push(device);
        }
      });
      return device;
    } catch (error) {
      this.handleError(error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async getDeviceByPimiosId(pimiosId) {
    const device = this.devices.find((d) => d.pimios_id === pimiosId);

    if (device) {
      console.log("There is a device in this cache: ", device);
      return device;
    }

    console.log(
      "New device suspected that is not in the frontend's cache, calling backend's route to get information!"
    );

    try {
      const newDevice = await apiHandler.get(
        `api/devices/pimios-id/${pimiosId}`
      );
      console.log("Here is the new device response: ", newDevice);

      runInAction(() => {
        this.devices.push(newDevice);
      });

      return newDevice;
    } catch (error) {
      console.log("Error fetching from backend!", error);
      return null;
    }
  }

  async updateDevice(deviceId, deviceData) {
    this.setLoading(true);
    try {
      const updatedDevice = await apiHandler.put(
        `/api/devices/${deviceId}`,
        deviceData
      );
      runInAction(() => {
        this.selectedDevice = updatedDevice;
        this.devices = this.devices.map((device) =>
          device.id === deviceId ? updatedDevice : device
        );
      });
      return updatedDevice;
    } catch (error) {
      this.handleError(error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async createDevice(deviceData) {
    this.setLoading(true);
    const userId =
      authStore.user?.userId || authStore.user?.id || authStore.user?.userID;
    console.log("Here is userId: ", userId);

    const payload = {
      ...deviceData,
      user_id: userId,
    };

    try {
      const newDevice = await apiHandler.post("/api/devices", payload);
      runInAction(() => {
        this.devices.unshift(newDevice);
      });
      return newDevice;
    } catch (error) {
      this.handleError(error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deleteDevice(deviceId) {
    this.setLoading(true);
    try {
      await apiHandler.delete(`/api/devices/${deviceId}`);
      runInAction(() => {
        this.devices = this.devices.filter((device) => device.id !== deviceId);
        if (this.selectedDevice?.id === deviceId) {
          this.selectedDevice = null;
        }
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  deleteDeviceNameFromCache(deviceName) {
    const isDevice = this.devices.find((d) => d.name === deviceName);

    if (!isDevice) {
      console.log("No device in cache!");
      return;
    }

    console.log(`There is a ${deviceName} in cache!`);

    runInAction(() => {
      this.devices = this.devices.filter((d) => d.name !== deviceName);
      console.log(this.devices.length);
      console.log(`Deleted device with name: ${deviceName}`);
    });
  }

  setLoading(loading) {
    this.isLoading = loading;
  }

  handleError(error) {
    runInAction(() => {
      console.log(error);
      const status = error?.response?.status ?? null;
      const data = error?.response?.data;
      console.log(data.message);

      // const serverMsg =
      //   (data && (data.message || data.error || data.detail)) ??
      //   (typeof data === "string" ? data : null);

      const serverMessage = data.message ? data.message : null;

      this.error = status || error.message || "An unexpected error occurred";
      this.errorMessage = serverMessage || null;
    });
  }

  clearError() {
    this.error = null;
    this.errorMessage = null;
  }

  setSelectedDevice(device) {
    this.selectedDevice = device;
  }

  clearSelectedDevice() {
    this.selectedDevice = null;
  }

  getDeviceName(id) {
    return this.deviceNameMap[id] || `Pending Device`;
  }

  async refreshDeviceNamesIncrementally() {
    try {
      const url = `/api/devices${this.lastFetchedDeviceId ? `?since_id=${this.lastFetchedDeviceId}` : ""}`;
      const newDevices = await apiHandler.get(url);

      runInAction(() => {
        newDevices.forEach((d) => {
          if (d.pimios_id) {
            this.deviceNameMap[d.pimios_id.trim()] = d.name?.trim() || "";
            if (d.id > this.lastFetchedDeviceId) {
              this.lastFetchedDeviceId = d.id;
            }
          }
        });
      });

      // if (Array.isArray(newDevices) && newDevices.length > 0) {
      //   console.log(
      //     `🔄 Updated device name map with ${newDevices.length} new device(s)`
      //   );
      // }
    } catch (err) {
      console.error("❌ Failed to fetch new device names:", err.message);
    }
  }

  startDeviceNameAutoRefresh() {
    //
    this.refreshDeviceNamesIncrementally();
    this.deviceNameRefreshInterval = setInterval(
      () => this.refreshDeviceNamesIncrementally(),
      2 * 60 * 1000
    );
  }

  stopDeviceNameAutoRefresh() {
    if (this.deviceNameRefreshInterval) {
      clearInterval(this.deviceNameRefreshInterval);
      this.deviceNameRefreshInterval = null;
    }
  }
}

const deviceStore = new DeviceStore();
export default deviceStore;
